import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import type { RecognitionEngine } from '../recognitionEngine';
import { RELIABLE_AFTER_HANDS, type EngineAccuracy } from '../recognitionAccuracy';
import {
  estimateCostUsd,
  summariseUsageLog,
  type CostRates,
  type UsageLogEntry,
} from '../usageLog';

export interface UsageLogViewActions {
  saveCostRates(rates: CostRates | undefined): void;
  clearLog(): void;
  setRecognitionEngine(engine: RecognitionEngine): void;
  back(): void;
}

const OUTCOME_LABELS = {
  success: 'Success',
  insufficient_credit: 'Out of credit',
  rate_limited: 'Rate limited',
  other: 'Other error',
} as const;

export interface AccuracyView {
  byEngine: Record<RecognitionEngine, EngineAccuracy>;
  /** Scans waiting to be saved as a round. */
  waiting: number;
}

const percent = (part: number, whole: number): string => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

function accuracyColumn(a: EngineAccuracy): string[] {
  const perHand = a.handsSaved > 0 ? (a.corrections / a.handsSaved).toFixed(1) : '—';
  const flagged = a.wrongTiles > 0 ? `${a.wrongTilesFlagged} of ${a.wrongTiles}` : a.handsSaved > 0 ? 'none wrong' : '—';
  return [
    String(a.handsSaved),
    a.handsSaved > 0 ? `${a.exactHands} (${percent(a.exactHands, a.handsSaved)})` : '—',
    a.tilesTotal > 0 ? `${percent(a.tilesRight, a.tilesTotal)} (${a.tilesRight} of ${a.tilesTotal})` : '—',
    perHand,
    flagged,
    String(a.noHand + a.errors),
    String(a.discarded),
  ];
}

const ACCURACY_ROWS = [
  'Scanned hands saved as a round',
  'Needed no correction at all',
  'Tiles it got right',
  'Tiles corrected per hand',
  'Wrong tiles it had flagged to double-check',
  'Scans that found nothing / failed',
  'Scans discarded without a round',
];

function renderAccuracySection(accuracy: AccuracyView): string {
  const { byEngine } = accuracy;
  const columns = [accuracyColumn(byEngine['on-device']), accuracyColumn(byEngine.relay)];
  const enough = Math.max(byEngine['on-device'].handsSaved, byEngine.relay.handsSaved) >= RELIABLE_AFTER_HANDS;
  const rows = ACCURACY_ROWS.map((label, i) => `<tr><td>${label}</td><td>${columns[0]![i]}</td><td>${columns[1]![i]}</td></tr>`).join('');
  return `
      <h2>Recognition accuracy</h2>
      <p class="muted">
        Every time a scanned hand is saved as a round, the tiles the scan proposed are compared with the tiles
        that were finally saved — so a "correction" is a tile someone had to change. This device only: it counts
        scans saved on this device, plus scans from guest phones that were applied here. It cannot see other
        devices' own scans.
      </p>
      <table class="summary">
        <thead><tr><th></th><th>On-device</th><th>Relay</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      ${accuracy.waiting > 0 ? `<p class="muted">${accuracy.waiting} scan${accuracy.waiting === 1 ? ' is' : 's are'} still waiting to be saved as a round.</p>` : ''}
      ${enough ? '' : `<p class="muted">Not enough hands yet for these percentages to mean much — look again after ${RELIABLE_AFTER_HANDS}+ scanned hands.</p>`}
  `;
}

export function renderUsageLogView(
  root: HTMLElement,
  entries: UsageLogEntry[],
  rates: CostRates | undefined,
  currentEngine: RecognitionEngine,
  accuracy: AccuracyView,
  actions: UsageLogViewActions,
): void {
  const summary = summariseUsageLog(entries);
  const cost = rates ? estimateCostUsd(summary, rates) : undefined;

  const recent = [...entries]
    .slice(-20)
    .reverse()
    .map((e) => {
      const when = new Date(e.timestamp).toLocaleString();
      const tokens =
        e.inputTokens !== undefined || e.outputTokens !== undefined
          ? `${e.inputTokens ?? 0} in / ${e.outputTokens ?? 0} out`
          : '—';
      return `
        <tr>
          <td>${esc(when)}</td>
          <td>${OUTCOME_LABELS[e.outcome]}</td>
          <td>${e.imageCount}</td>
          <td>${tokens}</td>
        </tr>
      `;
    })
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Recognition Usage</h1>
      <p class="muted">A local log of every tile-recognition attempt this browser has made. Nothing here talks to your Anthropic account directly — it's just what this app has recorded.</p>

      <h2>Recognition engine</h2>
      <p class="muted">
        Which engine actually runs each scan — a manual switch, not an automatic per-photo fallback,
        so you can watch how either one is doing on its own before trusting it. On-device is free and
        instant but newer/less proven; the Anthropic relay is the original, paid, well-tested path.
      </p>
      <select id="engineSelect">
        <option value="on-device" ${currentEngine === 'on-device' ? 'selected' : ''}>On-device (free)</option>
        <option value="relay" ${currentEngine === 'relay' ? 'selected' : ''}>Anthropic relay (paid)</option>
      </select>

      <table class="summary">
        <tbody>
          <tr><td>Total calls</td><td>${summary.totalCalls}</td></tr>
          <tr><td>Total photos</td><td>${summary.totalImages}</td></tr>
          <tr><td>Successful</td><td>${summary.byOutcome.success}</td></tr>
          <tr><td>Out of credit</td><td>${summary.byOutcome.insufficient_credit}</td></tr>
          <tr><td>Rate limited</td><td>${summary.byOutcome.rate_limited}</td></tr>
          <tr><td>Other errors</td><td>${summary.byOutcome.other}</td></tr>
          <tr><td>Total input tokens</td><td>${summary.totalInputTokens.toLocaleString()}</td></tr>
          <tr><td>Total output tokens</td><td>${summary.totalOutputTokens.toLocaleString()}</td></tr>
        </tbody>
      </table>

      ${renderAccuracySection(accuracy)}

      <h2>Cost estimate</h2>
      <p class="muted">Optional — enter your own current per-million-token rates to get a rough estimate. This is <strong>not</strong> an authoritative figure; check Anthropic's current pricing page before relying on it.</p>
      <label for="inputRate">Input $ / million tokens</label>
      <input type="number" id="inputRate" min="0" step="0.01" value="${rates?.inputPerMillionUsd ?? ''}" />
      <label for="outputRate">Output $ / million tokens</label>
      <input type="number" id="outputRate" min="0" step="0.01" value="${rates?.outputPerMillionUsd ?? ''}" />
      <button type="button" id="saveRatesBtn" class="secondary" style="margin-top:0.5rem">Save rates</button>
      ${
        cost !== undefined
          ? `<p class="result-box" style="margin-top:0.75rem">Rough estimate: <strong>$${cost.toFixed(4)}</strong> — approximate, confirm current pricing yourself.</p>`
          : ''
      }

      <h2>Recent attempts</h2>
      <table class="summary">
        <thead><tr><th>When</th><th>Outcome</th><th>Photos</th><th>Tokens</th></tr></thead>
        <tbody>${recent || '<tr><td colspan="4" class="muted">No recognition attempts logged yet.</td></tr>'}</tbody>
      </table>

      <div class="row" style="margin-top:1rem">
        <button type="button" id="clearLogBtn" class="danger">Clear Log</button>
        <button type="button" id="backBtn" class="secondary">Back</button>
      </div>
    </div>
  `;

  root.querySelector<HTMLSelectElement>('#engineSelect')!.addEventListener('change', (e) => {
    const value = (e.target as HTMLSelectElement).value;
    actions.setRecognitionEngine(value === 'relay' ? 'relay' : 'on-device');
  });

  root.querySelector<HTMLButtonElement>('#saveRatesBtn')!.addEventListener('click', () => {
    const inputRate = Number(root.querySelector<HTMLInputElement>('#inputRate')!.value);
    const outputRate = Number(root.querySelector<HTMLInputElement>('#outputRate')!.value);
    if (Number.isFinite(inputRate) && Number.isFinite(outputRate) && inputRate >= 0 && outputRate >= 0) {
      actions.saveCostRates({ inputPerMillionUsd: inputRate, outputPerMillionUsd: outputRate });
    } else {
      actions.saveCostRates(undefined);
    }
  });

  root.querySelector<HTMLButtonElement>('#clearLogBtn')!.addEventListener('click', () => {
    actions.clearLog();
  });

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => {
    actions.back();
  });
}
